import _merge from 'lodash/merge'
import _isEmpty from 'lodash/isEmpty'
import _zipObject from 'lodash/zipObject'
import * as T from '@/types'
import qs from 'qs'

import { Module, GetterTree, ActionTree, MutationTree, ModuleTree } from 'vuex'

export function mixin<S, R> () {
    type ParentState = Partial<S>
    type ChildState = Partial<S> & ParentState
    type FullChildState = ChildState & S

    // Have to assume getters, actions and mutations are 'any' state type
    interface ParentModule<S, R> extends Module<S,R> {
        namespaced?: boolean
        getters?: GetterTree<any, R>
        actions?: ActionTree<any, R>
        mutations?: MutationTree<any>
        modules?: ModuleTree<R>
    }

    interface ChildModule<S, R> extends ParentModule<S,R> {
        namespaced?: boolean
        getters?: GetterTree<FullChildState, R>
        actions?: ActionTree<FullChildState, R>
        mutations?: MutationTree<FullChildState>
        modules?: ModuleTree<R>
    }

    return function mixin<S, R> (mixins: ParentModule<ParentState, R>[], module: ChildModule<ChildState, R>) : ChildModule<S,R> {
        // loop through each mixin and merge properties
        const emptyStartingPoint: Module<S, R> = {}
    
        return _merge(mixins.reduce(
            (merged, toMerge) => {
                return _merge(merged, toMerge)
            },
            emptyStartingPoint,
        ), {...module})
    }
}

export function queryParamsSerializer (params: any) {
    const alphabeticalSort = (a: any, b: any) => {
        return a.localeCompare(b)
    }

    const filterKeys = [
        'case.name',
        'case.identifier',
        'creditor.name',
        'identifier',
        'name',
        'source.name',
        'source.source',
        'search',
        'tags[]',
    ]

    const filterFunc = (prefix: string, value: any) => {
        if (value === undefined || value === null || value === '') {
            return undefined
        }
        else if (prefix == 'sort') {
            return Object.keys(value).map((sortKey) => {
                return value[sortKey] ? `${sortKey}` : `-${sortKey}`
            }).join(',')
        }
        else if (typeof value === 'boolean') {
            return value ? 'NULL' : undefined
        }
        else if (typeof value === 'string' && filterKeys.includes(prefix)) {
            while (value.charAt(0) === '#') value = value.substr(1)
            return `~${value}`
        }
        return value
    }

    return qs.stringify(params, {
        arrayFormat: 'brackets',
        sort: alphabeticalSort,
        encode: false,
        encodeValuesOnly: true,
        filter: filterFunc,
    })
}

/**
 * Serializes filters for the query string.
 * 
 * @remarks
 * Deprecated in favour of queryParamsSerializer
 * 
 * @deprecated
 */
export function serializeSorts (sorts: T.Sorts): {sort: string} {
    return { sort: Object.keys(sorts).map((sortKey) => {
        return sorts[sortKey] ? `${sortKey}` : `-${sortKey}`
    }).join(',') }
}

/**
 * Serializes sorts for the query string.
 * 
 * @remarks
 * Deprecated in favour of queryParamsSerializer
 * 
 * @deprecated
 */
export function serializeFilters (filters: T.Filters | undefined): T.Filters | undefined {
    if (!filters) return
    let newFilters: {[key:string]: string} = {}
    for (let key of Object.keys(filters)){
        if (Array.isArray(filters[key])) {
            // @ts-ignore
            newFilters[key] = `${filters[key].join('&tags[]=')}`
        } else if (typeof filters[key] === 'boolean'){
            if (filters[key] === true) newFilters[key] = 'NULL'
        } else {
            newFilters[key] = `~${filters[key]}`
        }
    }
    return newFilters
}

/**
 * Deserializes sorts from the query string.
 * 
 * @remarks
 * Deserializes sorts from a string query to a Vuex friendly object representation.
 * Specific to CreditorDesk's API implementation {@link https://app.swaggerhub.com/apis/HubSolv/CreditorDesk/1.0.0#/Creditor/creditorListFiles}
 * 
 * @param sorts: a string representating sort parameters
 * @returns An object in the form { key : ascending } i.e { createdAt: false }, or an empty object.
 */
export function deserializeSorts (sorts: string): T.Sorts {
    if (!sorts || sorts === '') return {}
    const sortKeys = sorts.split(',').map((sortKey: string) => {
        if (sortKey.startsWith('-')) return sortKey.slice(1)
        else return sortKey
    })
    const sortValues = sorts.split(',').map((sort: string) => !sort.startsWith('-'))
    return _zipObject(sortKeys, sortValues)
}

/**
 * Deserializes filters from the query string
 * 
 * @remarks
 * Deserializes filters from a string query to a Vuex friendly object representation.
 * Specific to CreditorDesk's API implementation {@link https://app.swaggerhub.com/apis/HubSolv/CreditorDesk/1.0.0#/Creditor/creditorListFiles}
 * 
 * @param filters: an object of filter keys with string values 
 * @returns An object of filter keys and values converted to the correct type
 */
export function deserializeFilters (filters:{[key:string]: string | string[] }): T.Filters {
    if (!filters || _isEmpty(filters)) return {}
    const newFilters: any = {}
    Object.keys(filters).forEach((key: string) => {
        const newKey = key.endsWith('[]') ? key.slice(0,-2) : key
        const value = filters[key]
        if (key.endsWith('[]')) {
            if (Array.isArray(value)){
                newFilters[newKey] = value.map((x) => x.startsWith('~') ? x.slice(1) : x)
            } else {
                newFilters[newKey] = value.startsWith('~') ? [value.slice(1)] : [value]
            }
        } else if (typeof value === 'boolean') {
            newFilters[newKey] = true
        } else if (value === 'NULL') {
            newFilters[newKey] = true
        } else if (typeof value === 'string' && value.startsWith('~')) {
            newFilters[newKey] = value.slice(1)
        } else {
            newFilters[newKey] = value
        }
    })
    return newFilters
    // const filterKeys = Object.keys(filters).map((key: string) => {
    //     return key.endsWith('[]') ? key.slice(0,-2) : key
    // })
    // const filterValues = Object.values(filters).map((value: string | string[]) => {
    //     if (Array.isArray(value)) {
    //         console.log('IS ARRAY')
    //         return value.map(x => x.startsWith('~') ? x.slice(1) : x)
    //     } else if (typeof value === 'boolean') {
    //         return true
    //     } else if (value === 'NULL') {
    //         return true
    //     } else if (value.startsWith('~')) {
    //         return value.slice(1)
    //     } else {
    //         return value
    //     }
    // })
    // return _zipObject(filterKeys, filterValues)
}
